import useMyBhStore from "@app/store/useMyBhStore";
import {
  formatFollowResponse,
  GetUserInfo,
  HandleFollowTopic,
  HandleUnFollowTopic,
} from "@helper/baasHelper";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBHCustomEventAnalyticsCall } from "./useAnalyticsCall/useBHCustomEventAnalyticsCall";

type UseFollowTagsReturn = {
  visitorId: string;
  followedTags: string[];
  isFollowing: (topicId: string) => boolean;
  toggleFollow: (topicId: string, topicTitle: string) => Promise<void>;
  loadingTopicId: string | null;
  showToast: boolean;
};

type UseFollowTagsProps = {
  onRequireLogin?: () => void;
};

export default function useFollowTags({
  onRequireLogin,
}: UseFollowTagsProps): UseFollowTagsReturn {
  const [followedTags, setFollowedTags] = useState<string[]>([]);
  const [loadingTopicId, setLoadingTopicId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  const requestInFlight = useRef(false);

  const currentUser = GetUserInfo();
  const visitorId = currentUser.visitorId;

  const topicResponse = useMyBhStore((state) => state.tags);
  const bhCustomEvent = useBHCustomEventAnalyticsCall();

  // ================= SYNC RESPONSE =================
  useEffect(() => {
    const tags = formatFollowResponse(topicResponse);
    if (tags.length) {
      setFollowedTags(tags);
    }
  }, [topicResponse]);

  // ================= HELPERS =================
  const isFollowing = useCallback(
    (topicId: string) => followedTags.includes(topicId),
    [followedTags],
  );

  const userKeywords = useMemo(
    () => topicResponse.map((t) => t.name.toLowerCase()).join("|"),
    [topicResponse],
  );

  const pushAnalyticsEvent = useCallback(
    (topicTitle: string, action: "follow" | "unfollow") => {
      bhCustomEvent(
        "mybh",
        action,
        `topic::${topicTitle}`,
        undefined,
        undefined,
        undefined,
        undefined,
        userKeywords,
      );
    },
    [bhCustomEvent, userKeywords],
  );

  // ================= TOGGLE =================
  const toggleFollow = useCallback(
    async (topicId: string, topicTitle: string) => {
      if (!visitorId) {
        onRequireLogin?.();
        return;
      }

      if (!topicId || requestInFlight.current) return; // prevent race condition

      requestInFlight.current = true;
      setLoadingTopicId(topicId);

      try {
        const alreadyFollowing = isFollowing(topicId);
        const isFollow = !alreadyFollowing;

        const handler = isFollow ? HandleFollowTopic : HandleUnFollowTopic;
        const action = isFollow ? "follow" : "unfollow";

        const isSuccess = await handler(
          topicId,
          visitorId,
          topicTitle,
          () => {},
        );

        if (!isSuccess) return;

        setFollowedTags((prev) =>
          isFollow
            ? [...new Set([...prev, topicId])]
            : prev.filter((id) => id !== topicId),
        );

        // trigger toast only on follow
        if (isFollow) {
          setShowToast(true);
        }

        pushAnalyticsEvent(topicTitle, action);
      } catch (err) {
        console.error("Follow toggle failed", err);
      } finally {
        requestInFlight.current = false;
        setLoadingTopicId(null);
      }
    },
    [visitorId, isFollowing, onRequireLogin, pushAnalyticsEvent],
  );

  // reset toast trigger (so it can fire again)
  useEffect(() => {
    if (showToast) {
      const t = setTimeout(() => {
        setShowToast(false);
      }, 0);
      return () => {
        clearTimeout(t);
      };
    }
  }, [showToast]);

  return {
    visitorId,
    followedTags,
    isFollowing,
    toggleFollow,
    loadingTopicId,
    showToast,
  };
}
